import { alpha, Box, Grid, Typography, ThemeProvider } from "@mui/material";
import useNavBar from "../../context/NavBar/useNavBar";
import { isDesktop, trakrefFontFamily, trakrefFontFamilySemiBold, customTheme } from "../../utils";
import { useEffect, useState, useRef, useCallback } from "react";
import { Black, DividerColor, Grey4, trakrefDisabledBlue, trakrefBlue } from "../../color_utils";
import Enums from "../../enums";
import UserInfo from "../../UserInfo";
const SelectAccount = () => {
    const { instances, getData, changeInstance } = useNavBar();
    const [availableInstances, setAvailableInstances] = useState([]);
    const [typedChars, setTypedChars] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const itemRefs = useRef([]);
    const timeoutRef = useRef(null);
    useEffect(() => {
        if (instances?.length) {
            var filteredInstances = instances;
            if (!isDesktop) {
                if (UserInfo.isSystemAdministrator() || UserInfo.isNativeServiceProvider()) {
                    filteredInstances = filteredInstances.filter(instance => instance.accountTypeId === Enums.AccountTypes.Tenant);
                }
            }
            setAvailableInstances(filteredInstances);
        }
    }, [instances]);
    useEffect(() => {
        getData();
    }, []);

    const findMatch = useCallback((searchStr, start = 0) => {
        for (let i = 0; i < availableInstances.length; i++) {
            const idx = (start + i) % availableInstances.length;
            if (availableInstances[idx].name.toLowerCase().startsWith(searchStr)) return idx;
        }
        return -1;
    }, [availableInstances]);

    const handleKeyDown = useCallback((e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        if (e.key === 'Enter' && focusedIndex >= 0) {
            changeInstance(availableInstances[focusedIndex].id);
            return;
        }
        if (e.key.length !== 1) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        const char = e.key.toLowerCase();
        let search, matchIndex;

        if (typedChars === char) {
            search = char;
            matchIndex = findMatch(char, (focusedIndex + 1) % availableInstances.length);
        } else {
            search = typedChars + char;
            matchIndex = findMatch(search);
            if (matchIndex === -1) {
                search = char;
                matchIndex = findMatch(char);
            }
        }

        setTypedChars(search);
        if (matchIndex !== -1) {
            setFocusedIndex(matchIndex);
            itemRefs.current[matchIndex]?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
        }

        // Reset typed characters after 1 second of inactivity
        timeoutRef.current = setTimeout(() => setTypedChars(""), 1000);
    }, [typedChars, availableInstances, focusedIndex, changeInstance, findMatch]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [handleKeyDown]);

    return (
        <ThemeProvider theme={customTheme}>
        <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{
                minHeight: { md: "100vh" },
            }}
        >
            <Grid
                container
                direction={{ xs: "column", md: "row" }}
                sx={{
                    width: { xs: "100%", md: "800px" },
                    boxSizing: { xs: "content-box", md: "border-box" },
                    height: { xs: "none", md: "350px" },
                    borderRadius: { xs: 0, md: "8px" },
                    border: { xs: 0, md: `1px solid ${DividerColor}` },
                }}
            >
                <Grid item xs={6} sm={6} md={6} sx={{ padding: "20px" }} >
                    <Typography
                        variant='h5'
                        style={{
                            marginBottom: '10px',
                            paddingBottom: '2%',
                            color: '#1A173B',
                            fontFamily: trakrefFontFamilySemiBold
                        }}
                    >
                        Choose an account
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontFamily: trakrefFontFamily,
                            letterSpacing: "1px",
                            marginBottom: "10px",
                        }}
                    >
                        This login is linked to more than one account. Select the one you'd like to use. You can switch anytime.
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={3}
                    md={6}
                    sx={{
                        marginTop: { xs: "16px", md: 0 },
                        padding: "8px",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: { xs: "none", md: "350px" },
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "scroll",
                            paddingRight: "8px",
                            "&::-webkit-scrollbar": {
                                width: "10px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: alpha(trakrefDisabledBlue, 0.7),
                                borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                backgroundColor: alpha(trakrefDisabledBlue, 0.7),
                            },
                        }}
                    >
                        <Grid container direction="column">
                            {availableInstances.map((instance, index) => (
                                <Grid
                                    item
                                    key={instance.id}
                                    ref={el => itemRefs.current[index] = el}
                                    onClick={() => changeInstance(instance.id)}
                                    sx={{
                                        cursor: "pointer",
                                        borderTop: `1.5px solid ${DividerColor}`,
                                        marginLeft: "10px",
                                        padding: "16px",
                                        transition: "background 0.2s",
                                        backgroundColor: focusedIndex === index ? alpha(trakrefBlue, 0.1) : "transparent",
                                        "&:hover": {
                                            backgroundColor: focusedIndex === index ? alpha(trakrefBlue, 0.15) : Grey4,
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: Black,
                                            fontSize: "16px",
                                            fontFamily: trakrefFontFamily,
                                            fontWeight: 400,
                                            letterSpacing: "1px",
                                            marginLeft: "10px",
                                        }}
                                    >
                                        {instance.name}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
        </ThemeProvider>
    );
}

export default SelectAccount;